import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Spinner } from "@/components/ui/spinner"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        tertiary: "bg-tertiary text-tertiary-foreground hover:bg-tertiary/90",
      },
      size: {
        default: "h-10 px-5 py-2 text-base font-medium [&_svg]:size-5", // Label Large
        sm: "h-9 rounded-full px-4 text-sm font-medium [&_svg]:size-5", // Label Medium
        lg: "h-12 rounded-full px-7 text-base font-medium [&_svg]:size-6", // Label Large
        mini: "h-8 rounded-full px-3.5 text-sm font-medium [&_svg]:size-4", // Label Medium
        xl: "h-14 rounded-full px-7 text-base font-medium [&_svg]:size-6",
        icon: "h-10 w-10 [&_svg]:size-5", // 20px
        "icon-sm": "h-9 w-9 [&_svg]:size-5", // 20px
        "icon-lg": "h-12 w-12 [&_svg]:size-6", // 24px (Capped)
        "icon-mini": "h-8 w-8 [&_svg]:size-4", // 16px
        "icon-xl": "h-14 w-14 [&_svg]:size-6", // 24px (Capped)
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? <Spinner variant="inherit" className="size-5" /> : children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
